import type { UserLoginRo } from 'src/app/services/api.generated';

export type EditorHandoffSession = {
  token: string;
  user: UserLoginRo;
};

const exchanges = new Map<string, Promise<EditorHandoffSession>>();

function apiBaseUrl(): string {
  const configured =
    process.env.NODE_ENV === 'development'
      ? process.env.REACT_APP_DEVELOPMENT_BASE_URL
      : process.env.REACT_APP_PRODUCTION_BASE_URL;
  if (!configured || configured === 'auto') {
    return window.location.origin;
  }
  return configured.replace(/\/$/, '');
}

export function handoffCodeFromHash(hash: string): string | null {
  const raw = hash.startsWith('#') ? hash.slice(1) : hash;
  if (!raw) return null;
  const code = new URLSearchParams(raw).get('handoff')?.trim();
  return code || null;
}

export function hashCarriesLegacyToken(hash: string): boolean {
  const raw = hash.startsWith('#') ? hash.slice(1) : hash;
  if (!raw) return false;
  const params = new URLSearchParams(raw);
  return Boolean(params.get('token') || params.get('user'));
}

async function postHandoff(code: string): Promise<EditorHandoffSession> {
  const response = await fetch(`${apiBaseUrl()}/api/v1/auth/handoff`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ handoff: code }),
  });
  if (!response.ok) {
    throw new Error('Handoff failed');
  }
  const session = (await response.json()) as EditorHandoffSession;
  if (!session?.token || !session.user) {
    throw new Error('Handoff did not return a session');
  }
  return { token: session.token, user: session.user };
}

/** One network exchange per code, including a strict-mode remount. */
export function exchangeEditorHandoff(
  code: string,
): Promise<EditorHandoffSession> {
  const existing = exchanges.get(code);
  if (existing) return existing;
  const pending = postHandoff(code);
  exchanges.set(code, pending);
  return pending;
}
